import * as React from 'react';
import { CalendarIcons } from 'owa-calendar-icons';
import { calendarPrintPanelPrintLabel } from 'owa-locstrings/lib/strings/calendarprintpanelprintlabel.locstring.json';
import type { CalendarSurfaceStore } from 'owa-calendar-surface-dategrid';
import { ControlIcons } from 'owa-control-icons';
import { DateRange, getNextDateRange, getPreviousDateRange } from 'owa-datetime-utils';
import dateRangeToDisplayString from 'owa-timestrip/lib/utils/dateRangeToDisplayString';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import {
    DisplayOption,
    FilterableCommandBar,
    FilterableMenuItem,
    FilterableMenuItemArgs,
} from 'owa-filterable-menu';
import { formatMonthDayYear, today } from 'owa-datetime';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { goToNextDayText } from 'owa-locstrings/lib/strings/gotonextdaytext.locstring.json';
import { goToPreviousDayText } from 'owa-locstrings/lib/strings/gotopreviousdaytext.locstring.json';
import goToToday from '../actions/goToToday';
import loc, { format } from 'owa-localize';
import { logUsage } from 'owa-analytics';
import { observer } from 'mobx-react-lite';
import { Timestrip } from 'owa-timestrip';
import { updateModuleCommand } from 'owa-publishedcalendar-settings';
import {
    commandBarEllipsisAriaLabel,
    goToTodayDateText,
    todayStringLowerCaseText,
    switchToDayText,
    switchToWeekText,
    switchToMonthText,
    calendarSurfaceRegionName,
    goToPreviousWeekStringText,
    goToNextWeekStringText,
    goToPreviousMonthText,
    goToNextMonthStringText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

import styles from './PublishedCalendar.scss';

const SINGLE_DAY_VIEW_ONE_DAY = 1;

export interface PublishedCalendarCommandBarProps extends React.HTMLProps<HTMLDivElement> {
    store: CalendarSurfaceStore;
    onDateRangeChange: (dateRange: DateRange) => void;
    onDateRangeTypeChange: (
        dateRangeType: DateRangeType,
        dateRange: DateRange,
        numOfDaysInDayRange?: number
    ) => void;
}

/**
 * The command bar that shows up on top of Published Calendar surface.
 */
export default observer(function PublishedCalendarCommandBar(
    props: PublishedCalendarCommandBarProps
) {
    let commandBarItems = () => {
        let itemList: FilterableMenuItem[] = [];

        const userOptions = getUserConfiguration().UserOptions;
        const todayDate = today();
        const goToTodayLabel = format(loc(goToTodayDateText), '\n' + formatMonthDayYear(todayDate));
        const currentDateRange = getCurrentRange(props.store.dateRangeType);

        let currentDateRangeType = currentDateRange.dateRangeType;

        const previousDateRange = dateRangeToDisplayString(
            getPreviousDateRange(props.store.dateRange, currentDateRangeType),
            currentDateRangeType,
            userOptions.WeekStartDay,
            userOptions.ShowWeekNumbers,
            userOptions.FirstWeekOfYear as any,
            false
        );
        const nextDateRange = dateRangeToDisplayString(
            getNextDateRange(props.store.dateRange, currentDateRangeType),
            currentDateRangeType,
            userOptions.WeekStartDay,
            userOptions.ShowWeekNumbers,
            userOptions.FirstWeekOfYear as any,
            false
        );

        const previousLabel = format(currentDateRange.previousLabel, '\n' + previousDateRange);
        const nextLabel = format(currentDateRange.nextLabel, '\n' + nextDateRange);

        let isSelectedDay =
            currentDateRangeType == DateRangeType.Day ? styles.dateRangeSelected : null;
        let isSelectedWeek =
            currentDateRangeType == DateRangeType.Week ? styles.dateRangeSelected : null;
        let isSelectedMonth =
            currentDateRangeType == DateRangeType.Month ? styles.dateRangeSelected : null;

        itemList = itemList.concat([
            new FilterableMenuItem({
                // Today button
                [FilterableMenuItemArgs.name]: loc(todayStringLowerCaseText),
                [FilterableMenuItemArgs.icon]: ControlIcons.GotoToday,
                [FilterableMenuItemArgs.onClick]: switchToToday,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.ariaLabel]: goToTodayLabel,
                [FilterableMenuItemArgs.title]: goToTodayLabel,
            }),
            new FilterableMenuItem({
                // Up or Back button
                [FilterableMenuItemArgs.icon]: isSelectedMonth
                    ? ControlIcons.Up
                    : ControlIcons.Back,
                [FilterableMenuItemArgs.onClick]: onPrevious(props),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.ariaLabel]: previousLabel,
                [FilterableMenuItemArgs.title]: previousLabel,
                [FilterableMenuItemArgs.className]: 'flipForRtl',
            }),
            new FilterableMenuItem({
                // Down or Forward button
                [FilterableMenuItemArgs.icon]: isSelectedMonth
                    ? ControlIcons.Down
                    : ControlIcons.Forward,
                [FilterableMenuItemArgs.onClick]: onNext(props),
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.ariaLabel]: nextLabel,
                [FilterableMenuItemArgs.title]: nextLabel,
                [FilterableMenuItemArgs.className]: 'flipForRtl',
            }),
            new FilterableMenuItem({
                // Timestrip
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Left,
                [FilterableMenuItemArgs.onRender]: item => (
                    <Timestrip
                        className={styles.timestrip}
                        onDateRangeChange={props.onDateRangeChange}
                        aria-setsize={item['aria-setsize']}
                        aria-posinset={item['aria-posinset']}
                        dateRange={props.store.dateRange}
                        dateRangeType={props.store.dateRangeType}
                        weekStartDay={props.store.userOptions.weekStartDay}
                        showWeekNumbers={props.store.userOptions.showWeekNumbers}
                        firstWeekOfYear={props.store.userOptions.firstWeekOfYear}
                        workDays={props.store.userOptions.workDays}
                        useWeekDay={false}
                        ariaRole="menuitem"
                    />
                ),
            }),
            new FilterableMenuItem({
                // DateRangeType menu
                [FilterableMenuItemArgs.name]: currentDateRange.name,
                [FilterableMenuItemArgs.icon]: currentDateRange.icon,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Right,
                [FilterableMenuItemArgs.menuContent]: [
                    new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: loc(switchToDayText),
                        [FilterableMenuItemArgs.onClick]: () =>
                            switchToSingleDayView(SINGLE_DAY_VIEW_ONE_DAY, props),
                        [FilterableMenuItemArgs.title]: loc(switchToDayText),
                        [FilterableMenuItemArgs.className]: isSelectedDay,
                        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarDay,
                    }),
                    new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: loc(switchToWeekText),
                        [FilterableMenuItemArgs.onClick]: switchToWeek(props),
                        [FilterableMenuItemArgs.title]: loc(switchToWeekText),
                        [FilterableMenuItemArgs.className]: isSelectedWeek,
                        [FilterableMenuItemArgs.icon]: CalendarIcons.CalendarWeek,
                    }),
                    new FilterableMenuItem({
                        [FilterableMenuItemArgs.name]: loc(switchToMonthText),
                        [FilterableMenuItemArgs.onClick]: switchToMonth(props),
                        [FilterableMenuItemArgs.title]: loc(switchToMonthText),
                        [FilterableMenuItemArgs.className]: isSelectedMonth,
                        [FilterableMenuItemArgs.icon]: ControlIcons.Calendar,
                    }),
                ],
                [FilterableMenuItemArgs.key]: 'surfaceCommandBar_rangeButton',
                [FilterableMenuItemArgs.title]: loc(calendarSurfaceRegionName),
            }),
            new FilterableMenuItem({
                // Print button
                [FilterableMenuItemArgs.name]: loc(calendarPrintPanelPrintLabel),
                [FilterableMenuItemArgs.icon]: ControlIcons.Print,
                [FilterableMenuItemArgs.onClick]: openPrintDialog,
                [FilterableMenuItemArgs.displayOption]: DisplayOption.Right,
            }),
        ]);

        itemList.forEach((item, index, array) => {
            if (item) {
                item['aria-setsize'] = array.length;
                item['aria-posinset'] = index + 1; //posinset is 1 based
            }
        });

        return itemList;
    };
    return (
        <FilterableCommandBar
            filterableItems={commandBarItems()}
            elipisisAriaLabel={loc(commandBarEllipsisAriaLabel)}
        />
    );
});

/**
 * Get the current date range
 */
function getCurrentRange(dateRangeType: DateRangeType) {
    switch (dateRangeType) {
        case DateRangeType.Day:
            return {
                name: loc(switchToDayText),
                icon: CalendarIcons.CalendarDay,
                dateRangeType: DateRangeType.Day,
                previousLabel: loc(goToPreviousDayText),
                nextLabel: loc(goToNextDayText),
            };
        case DateRangeType.WorkWeek:
        case DateRangeType.Week:
            return {
                name: loc(switchToWeekText),
                icon: CalendarIcons.CalendarWeek,
                dateRangeType: DateRangeType.Week,
                previousLabel: loc(goToPreviousWeekStringText),
                nextLabel: loc(goToNextWeekStringText),
            };
        case DateRangeType.Month:
            return {
                name: loc(switchToMonthText),
                icon: ControlIcons.Calendar,
                dateRangeType: DateRangeType.Month,
                previousLabel: loc(goToPreviousMonthText),
                nextLabel: loc(goToNextMonthStringText),
            };
    }
}

/**
 * Open the Print dialog
 */
function openPrintDialog() {
    updateModuleCommand('Open_PrintModal');
}

/**
 * When Previous is executed
 */
const onPrevious = (props: PublishedCalendarCommandBarProps) => (
    ev: React.MouseEvent<HTMLDivElement>
) => {
    logUsage('PublishedCalendarCommandBar_Previous', {
        currentDateRangeType: props.store.dateRangeType,
    });
    let newDateRange = getPreviousDateRange(props.store.dateRange, props.store.dateRangeType);

    let { onDateRangeChange } = props;
    onDateRangeChange?.(newDateRange);
};

/**
 * When Next is executed
 */
const onNext = (props: PublishedCalendarCommandBarProps) => (
    ev: React.MouseEvent<HTMLDivElement>
) => {
    logUsage('PublishedCalendarCommandBar_Next', {
        currentDateRangeType: props.store.dateRangeType,
    });
    let newDateRange = getNextDateRange(props.store.dateRange, props.store.dateRangeType);
    let { onDateRangeChange } = props;
    onDateRangeChange?.(newDateRange);
};

/**
 * Switch to Day View
 */
function switchToSingleDayView(
    numberOfDays: number,
    props: PublishedCalendarCommandBarProps
): void {
    let { onDateRangeTypeChange } = props;

    logUsage('PublishedCalendarCommandBar_switchToSingleDayView', { numberOfDays: numberOfDays });
    onDateRangeTypeChange?.(DateRangeType.Day, props.store.dateRange, numberOfDays);
}

/**
 * Switch to Week View
 */
const switchToWeek = (props: PublishedCalendarCommandBarProps) => (
    ev: React.MouseEvent<HTMLDivElement>
) => {
    let { onDateRangeTypeChange } = props;
    onDateRangeTypeChange?.(DateRangeType.Week, props.store.dateRange);
};

/**
 * Switch to Month View
 */
const switchToMonth = (props: PublishedCalendarCommandBarProps) => (
    ev: React.MouseEvent<HTMLDivElement>
) => {
    let { onDateRangeTypeChange } = props;
    onDateRangeTypeChange?.(DateRangeType.Month, props.store.dateRange);
};

/**
 * Switch to today
 */
function switchToToday() {
    goToToday();
}
